import { useQuery } from '@tanstack/react-query'
import { useId } from 'react'
import { lessonsQuery } from '@/api/lessons'
import type { AgeBand } from '@/api/stories'
import { Button } from '@/components/ui/button'
import type { LessonCard } from '@/schema/lessons'

export type ObjectiveSelection = { id: string; revision: number }

export function LessonDetails({ card, ageBand }: { card: LessonCard; ageBand: string }) {
  return <div className="space-y-2 text-sm text-ink-2">
    <p className="font-medium text-ink">{card.objective}</p>
    <p>{card.activities_by_band[ageBand as AgeBand]}</p>
    <p>Success: {card.success_criterion}</p>
    <p className="text-xs">{card.review_state === 'educator_reviewed' ? 'Educator-reviewed' : 'Source-grounded; not educator-reviewed'} · Revision {card.revision}</p>
    <ul className="space-y-1">{card.sources.map((source) => <li key={source.id}><a href={source.url} target="_blank" rel="noreferrer" className="underline underline-offset-4 focus-visible:focus-ring">{source.title}</a></li>)}</ul>
  </div>
}

export function LessonPicker({ subject, ageBand, value, onChange }: {
  subject: string; ageBand: AgeBand; value: ObjectiveSelection | null; onChange: (card: LessonCard | null) => void
}) {
  const id = useId()
  const query = useQuery({ ...lessonsQuery(subject, ageBand), retry: false })
  const cards = query.data?.items.filter((c) => c.subject === subject && c.age_bands.includes(ageBand)) ?? []
  const card = cards.find((c) => c.id === value?.id) ?? (cards.length === 1 ? cards[0] : undefined)
  const stale = value && (!card || card.id !== value.id || card.revision !== value.revision)
  return <section aria-label="Learning objective preview" className="space-y-3 rounded-xl border border-line p-4">
    {query.isPending ? <p role="status">Loading supported objectives…</p> : query.isError ? <div role="alert"><p>Learning objectives could not load.</p><Button onClick={() => void query.refetch()}>Retry objectives</Button></div> : <>
      <label htmlFor={id} className="block text-sm font-medium text-ink">Learning objective</label>
      <select id={id} aria-describedby={`${id}-help`} value={value?.id ?? ''} className="h-11 w-full rounded-xl border border-line-strong bg-paper px-3 text-sm text-ink focus-visible:focus-ring" onChange={(e) => onChange(cards.find((c) => c.id === e.target.value) ?? null)}>
        <option value="">Match my idea to a supported objective</option>
        {cards.map((c) => <option key={c.id} value={c.id}>{c.objective}</option>)}
      </select>
      <p id={`${id}-help`} className="text-xs text-ink-2">Choose the exact concept, or let preflight match your idea. Unsupported concepts are not replaced with another lesson.</p>
      {stale && <p role="alert">This objective changed or is unavailable. Choose a current objective.</p>}
      {!cards.length ? <p role="status">No supported objective for this subject and age yet. Choose another subject or use a non-teaching story topic.</p> : card ? <LessonDetails card={card} ageBand={ageBand} /> : null}
    </>}
  </section>
}
